import { Link } from 'react-router-dom';
import { getMenuItems } from '../modules/menu';
import { usePublicConfig, useFeatureEnabled } from '../hooks/usePublicConfig';
import { env } from '../config';

export const Footer = () => {
  const { data: publicConfig } = usePublicConfig();
  const newsletterOn = useFeatureEnabled('newsletter');
  const menuItems = getMenuItems(publicConfig?.modules, publicConfig?.menu);
  const siteName = publicConfig?.site?.name || env.VITE_SITE_NAME; // runtime site.name, env fallback

  // Split site name for branded styling (same pattern as Header)
  const nameParts = siteName.split(' ');
  const firstWord = nameParts[0];
  const restWords = nameParts.slice(1).join(' ');

  return (
    <footer className="bg-background-dark text-inverse-muted pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* Brand column */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-primary p-1.5 rounded-lg text-inverse">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <span className="text-xl font-bold tracking-tight text-inverse uppercase">
                {firstWord}
                {restWords && <span className="text-primary">{restWords}</span>}
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-faint">
              Professional web design, hosting, and technology consulting for growing businesses.
            </p>
            {/* Social icons */}
            <div className="flex gap-4">
              <a
                href="#"
                className="w-8 h-8 rounded-full bg-surface-dark flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Twitter"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
              </a>
              <a
                href="#"
                className="w-8 h-8 rounded-full bg-surface-dark flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              <a
                href="#"
                className="w-8 h-8 rounded-full bg-surface-dark flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Company links */}
          <div>
            <h4 className="text-inverse font-bold mb-6">Company</h4>
            <ul className="space-y-4 text-sm">
              {menuItems.map(item => {
                const to = item.path.startsWith('/') ? item.path : `/${item.path}`;
                return (
                  <li key={item.path}>
                    {item.external ? (
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        {item.title}
                      </a>
                    ) : (
                      <Link to={to} className="hover:text-primary transition-colors">
                        {item.title}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Newsletter - spans 2 cols on lg; runtime-gated (features.newsletter) */}
          {newsletterOn && (
          <div className="lg:col-span-2">
            <h4 className="text-inverse font-bold mb-6">Subscribe to Our Newsletter</h4>
            <p className="text-sm text-faint mb-6">
              Get the latest updates and insights delivered to your inbox.
            </p>
            <form className="flex gap-2" onSubmit={e => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                className="bg-surface-dark border-none rounded-lg p-3 flex-grow text-inverse placeholder:text-muted focus:ring-2 focus:ring-primary focus:outline-none"
              />
              <button
                type="submit"
                className="bg-primary text-inverse px-6 py-3 rounded-lg font-bold hover:bg-primary/90 transition-all"
              >
                Join
              </button>
            </form>
          </div>
          )}

        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-surface-dark text-center text-xs text-muted">
          <p>&copy; {new Date().getFullYear()} {siteName}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};
